import React, { useState, useEffect} from "react";
import {
  Box,
  Grid,
  Typography,
  Select,
  MenuItem,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Paper,
  Chip,
  Checkbox,
  FormHelperText,
} from "@mui/material";

const RoutingGuideForm = ({
  formValues,
  handleInputChange,
  consolidators,
  dcs,
  serviceLevels,
  carriers,
  stores = [],
  supplierdetail = [],
  onErrorChange, // New prop to communicate error state to parent
}) => {
  const [errors, setErrors] = useState({
    uMin: "",
    uMax: "",
    wtMin: "",
    wtMax: "",
  });

  const predefinedGroups = [
    { id: "ALL STORES", name: "ALL STORES" },
    { id: "ALL ECDC STORES", name: "ALL ECDC STORES" },
    { id: "ALL WCSC STORES", name: "ALL WCSC STORES" },
    { id: "ALL PNDC STORES", name: "ALL PNDC STORES" },
  ];

  React.useEffect(() => {
    if (!formValues.department_type) {
      handleInputChange({
        target: {
          name: "department_type",
          value: "list",
        },
      });
    }
  }, []);

    // Notify parent component about error state changes
    useEffect(() => {
      const hasErrors = Object.values(errors).some(error => error !== "");
      if (onErrorChange) {
        onErrorChange(hasErrors);
      }
    }, [errors, onErrorChange]);

  // Initialize selected stores from string or array
  const initializeSelectedStores = (formValues) => {
    if (!formValues.store_type) return [];

    if (Array.isArray(formValues.store_type)) {
      return formValues.store_type;
    }

    // Split the string by comma
    const storeTypes = formValues.store_type.split(",").map((s) => s.trim());

    return storeTypes.map((storeType) => {
      // Check if it's a predefined group
      const predefinedGroup = predefinedGroups.find(
        (g) => g.name === storeType
      );
      if (predefinedGroup) return predefinedGroup.id;

      // Check if it matches store name-id format
      const storeMatch = storeType.match(/^(.+)-(\d+)$/);
      if (storeMatch) {
        const storeId = storeMatch[2];
        const store = stores.find((s) => s.store_id.toString() === storeId);
        if (store) return storeId.toString();
      }

      return storeType;
    });
  };

  const [selectedStores, setSelectedStores] = useState(
    initializeSelectedStores(formValues)
  );

  const isValueSelected = (value) => {
    return selectedStores.some((selected) => {
      // For predefined groups
      if (selected === value) return true;

      // For individual stores, compare store IDs
      if (stores.find((s) => s.store_id.toString() === value)) {
        return selected === value;
      }

      return false;
    });
  };

  // Check if value is a float
  const isFloat = (value) => {
    if (!value) return false;
    const num = value.toString().replace(/,/g, "");
    return num.includes(".");
  };

  const parseNumericValue = (value) => {
    if (typeof value === "string") {
      return parseFloat(value.replace(/,/g, ""));
    }
    return value;
  };

  const validateRanges = (name, value) => {
    const newErrors = { ...errors };
    const uMin = parseNumericValue(formValues.u_min);
    const uMax = parseNumericValue(formValues.u_max);
    const wtMin = parseNumericValue(formValues.wt_min);
    const wtMax = parseNumericValue(formValues.wt_max);

    // Reset errors
    newErrors.uMin = "";
    newErrors.uMax = "";
    newErrors.wtMin = "";
    newErrors.wtMax = "";

    // Check for float values in u_min and u_max
    if (name === "u_min") {
      if (isFloat(value)) {
        newErrors.uMin = "Enter valid input";
      }
    }
    if (name === "u_max") {
      if (isFloat(value)) {
        newErrors.uMax = "Enter valid input";
      }
    }
    setErrors(newErrors);
  };

  const handleEnhancedInputChange = (event) => {
    const { name, value } = event.target;
    handleInputChange(event);
    validateRanges(name, value);
  };

  const handleStoreChange = (event) => {
    const selectedValues = event.target.value;

    // Convert both arrays to Sets for easier comparison
    const currentSet = new Set(selectedStores);
    const newSet = new Set(selectedValues);

    // Find items that were deselected (in current but not in new)
    const deselectedValues = [...currentSet].filter((x) => !newSet.has(x));
    // Find items that were selected (in new but not in current)
    const addedValues = [...newSet].filter((x) => !currentSet.has(x));

    if (deselectedValues.length > 0) {
      // Handle deselection
      const newSelectedStores = selectedStores.filter(
        (store) => !deselectedValues.includes(store)
      );
      setSelectedStores(newSelectedStores);

      // Update form values with store name-id format
      const storeDetails = newSelectedStores.map((value) => {
        const predefinedGroup = predefinedGroups.find((g) => g.id === value);
        if (predefinedGroup) {
          return predefinedGroup.name;
        }
        const store = stores.find((s) => s.store_id.toString() === value);
        if (store) {
          return `${store.name}-${store.store_id}`;
        }
        return value;
      });

      handleInputChange({
        target: {
          name: "store_type",
          value: storeDetails.length > 0 ? storeDetails.join(",") : "",
        },
      });
    } else if (addedValues.length > 0) {
      // Handle selection
      setSelectedStores(selectedValues);

      const storeDetails = selectedValues.map((value) => {
        const predefinedGroup = predefinedGroups.find((g) => g.id === value);
        if (predefinedGroup) {
          return predefinedGroup.name;
        }
        const store = stores.find((s) => s.store_id.toString() === value);
        if (store) {
          return `${store.name}-${store.store_id}`;
        }
        return value;
      });

      handleInputChange({
        target: {
          name: "store_type",
          value: storeDetails.join(","),
        },
      });
    }
  };

  const handleChipDelete = (storeToDelete) => {
    const newSelectedStores = selectedStores.filter(
      (store) => store !== storeToDelete
    );
    setSelectedStores(newSelectedStores);

    const storeDetails = newSelectedStores.map((value) => {
      const predefinedGroup = predefinedGroups.find((g) => g.id === value);
      if (predefinedGroup) {
        return predefinedGroup.name;
      }
      const store = stores.find((s) => s.store_id.toString() === value);
      if (store) {
        return `${store.name}-${store.store_id}`;
      }
      return value;
    });

    handleInputChange({
      target: {
        name: "store_type",
        value: storeDetails.length > 0 ? storeDetails.join(",") : "",
      },
    });
  };

  const renderValue = (selected) => (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
      {selected.map((value) => {
        const predefinedGroup = predefinedGroups.find((g) => g.id === value);
        if (predefinedGroup) {
          return (
            <Chip
              key={value}
              label={predefinedGroup.name}
              size="small"
              onDelete={() => handleChipDelete(value)}
            />
          );
        }

        const store = stores.find((s) => s.store_id.toString() === value);
        if (store) {
          return (
            <Chip
              key={value}
              label={`${store.name}-${store.store_id}`}
              size="small"
              onDelete={() => handleChipDelete(value)}
            />
          );
        }

        return (
          <Chip
            key={value}
            label={value}
            size="small"
            onDelete={() => handleChipDelete(value)}
          />
        );
      })}
    </Box>
  );

  return (
    <Paper elevation={0} sx={{ p: 3, minWidth: "600px" }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              backgroundColor: "#f5f5f5",
              p: 2,
              borderRadius: "8px",
            }}
          >
            <Typography
              sx={{ width: "180px", fontWeight: 600, color: "#2c3e50" }}
            >
              Ship From Zip1: *
            </Typography>
            <TextField
              name="ship_from_zip1"
              value={formValues.ship_from_zip1 || ""}
              onChange={handleInputChange}
              size="small"
              sx={{
                width: "180px",
                "& .MuiOutlinedInput-root": { backgroundColor: "white" },
              }}
            />
            <Typography sx={{ mx: 2 }}>-</Typography>
            <TextField
              name="ship_from_zip2"
              value={formValues.ship_from_zip2 || ""}
              onChange={handleInputChange}
              size="small"
              sx={{
                width: "180px",
                "& .MuiOutlinedInput-root": { backgroundColor: "white" },
              }}
            />
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              backgroundColor: "#f5f5f5",
              p: 2,
              borderRadius: "8px",
            }}
          >
            <Typography
              sx={{ width: "180px", fontWeight: 600, color: "#2c3e50" }}
            >
              Consolidator: *
            </Typography>
            <Select
              name="consolidator"
              value={formValues.consolidator || ""}
              onChange={handleInputChange}
              size="small"
              sx={{ width: "400px", backgroundColor: "white" }}
            >
              {consolidators.map((consolidator) => (
                <MenuItem
                  key={consolidator.facility_id}
                  value={consolidator.facility_id}
                >
                  {consolidator.name}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              backgroundColor: "#f5f5f5",
              p: 2,
              borderRadius: "8px",
            }}
          >
            <Typography
              sx={{ width: "180px", fontWeight: 600, color: "#2c3e50" }}
            >
              DC: *
            </Typography>
            <Select
              name="dc"
              value={formValues.dc || ""}
              onChange={handleInputChange}
              size="small"
              sx={{ width: "400px", backgroundColor: "white" }}
            >
              {dcs.map((dc) => (
                <MenuItem key={dc.facility_id} value={dc.facility_id}>
                  {dc.name}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Grid>

        {/* <Grid item xs={12}>
          
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              backgroundColor: "#f5f5f5",
              p: 2,
              borderRadius: "8px",
            }}
          >
            <Typography
              sx={{ width: "180px", fontWeight: 600, color: "#2c3e50" }}
            >
              Facility Type: *
            </Typography>
            <Select
              name="facility_type"
              value={formValues.facility_type || "Consolidator"}
              onChange={handleInputChange}
              size="small"
              sx={{ width: "200px", backgroundColor: "white" }}
            >
              <MenuItem value="Consolidator">Consolidator</MenuItem>
            </Select>
          </Box>
        </Grid> */}

        <Grid item xs={12}>
          <Box sx={{ backgroundColor: "#f5f5f5", p: 2, borderRadius: "8px" }}>
            <Box display="flex" alignItems="flex-start">
              <Typography
                sx={{ width: "180px", fontWeight: 600, color: "#2c3e50" }}
              >
                Store: *
              </Typography>
              <Select
                multiple
                value={selectedStores}
                onChange={handleStoreChange}
                size="small"
                renderValue={renderValue}
                sx={{ width: "400px", backgroundColor: "white" }}
              >
                {predefinedGroups.map((group) => (
                  <MenuItem key={group.id} value={group.id}>
                    <Checkbox checked={isValueSelected(group.id)} />
                    {group.name}
                  </MenuItem>
                ))}
                <MenuItem sx={{ borderTop: "1px solid #eee", pt: 1 }} disabled>
                  Individual Stores
                </MenuItem>
                {stores.map((store) => (
                  <MenuItem
                    key={store.store_id}
                    value={store.store_number.toString()}
                  >
                    <Checkbox
                      checked={isValueSelected(store.store_number.toString())}
                    />
                    {`${store.name}-${store.store_number}`}
                  </MenuItem>
                ))}
                {/* Add a section for custom values that don't match predefined options */}
                {selectedStores.some(
                  (store) =>
                    !predefinedGroups.find((g) => g.id === store) &&
                    !stores.find((s) => s.store_number.toString() === store)
                ) && (
                  <>
                    <MenuItem
                      sx={{ borderTop: "1px solid #eee", pt: 1 }}
                      disabled
                    >
                      Custom Stores
                    </MenuItem>
                    {selectedStores
                      .filter(
                        (store) =>
                          !predefinedGroups.find((g) => g.id === store) &&
                          !stores.find(
                            (s) => s.store_number.toString() === store
                          )
                      )
                      .map((customStore) => (
                        <MenuItem key={customStore} value={customStore}>
                          <Checkbox checked={true} />
                          {customStore}
                        </MenuItem>
                      ))}
                  </>
                )}
              </Select>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ backgroundColor: "#f5f5f5", p: 2, borderRadius: "8px" }}>
            <Box display="flex" alignItems="flex-start">
              <Typography
                sx={{ width: "180px", fontWeight: 600, color: "#2c3e50" }}
              >
                Department: *
              </Typography>
              <Box>
                <FormControl>
                  <RadioGroup
                    row
                    name="department_type"
                    value={formValues.department_type || "list"}
                    onChange={handleInputChange}
                    sx={{ gap: 2 }}
                  >
                    <FormControlLabel
                      value="All Departments"
                      control={<Radio />}
                      label="All Departments"
                      sx={{
                        "& .MuiFormControlLabel-label": {
                          color: "#2c3e50",
                          fontWeight: 500,
                        },
                      }}
                    />
                    {supplierdetail && supplierdetail.commodity_code_name && (
                      <FormControlLabel
                        value={`All ${supplierdetail.commodity_code} Commodity Departments`}
                        control={<Radio />}
                        label={`All ${supplierdetail.commodity_code} Commodity Departments`}
                        sx={{
                          "& .MuiFormControlLabel-label": {
                            color: "#2c3e50",
                            fontWeight: 500,
                          },
                        }}
                      />
                    )}
                    <FormControlLabel
                      value="list"
                      control={<Radio />}
                      label="Other: List of Commodity Dept's"
                      sx={{
                        "& .MuiFormControlLabel-label": {
                          color: "#2c3e50",
                          fontWeight: 500,
                        },
                      }}
                    />
                  </RadioGroup>
                </FormControl>
                {(formValues.department_type === "list" ||
                  !formValues.department_type) && (
                  <TextField
                    multiline
                    rows={3}
                    name="department_list"
                    value={formValues.department_list || ""}
                    onChange={handleInputChange}
                    placeholder="Enter departments (comma-separated)"
                    size="small"
                    sx={{
                      width: "400px",
                      mt: 2,
                      "& .MuiOutlinedInput-root": { backgroundColor: "white" },
                    }}
                  />
                )}
              </Box>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              backgroundColor: "#f5f5f5",
              p: 2,
              borderRadius: "8px",
            }}
          >
            <Typography
              sx={{ width: "180px", fontWeight: 600, color: "#2c3e50" }}
            >
              Unit Type: *
            </Typography>
            <Select
              name="unit_type"
              value={formValues.unit_type || "Carton"}
              onChange={handleInputChange}
              size="small"
              sx={{ width: "200px", backgroundColor: "white" }}
            >
              <MenuItem value="Carton">Carton</MenuItem>
              <MenuItem value="N/A">N/A</MenuItem>
              <MenuItem value="Pallet">Pallet</MenuItem>
              <MenuItem value="$ Value at Cost">$ Value at Cost</MenuItem>
            </Select>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              backgroundColor: "#f5f5f5",
              p: 2,
              borderRadius: "8px",
            }}
          >
            <Typography
              sx={{ width: "180px", fontWeight: 600, color: "#2c3e50" }}
            >
              UMin: *
            </Typography>
            <Box>
              <TextField
                name="u_min"
                value={formValues.u_min}
                onChange={handleEnhancedInputChange}
                size="small"
                error={!!errors.uMin}
                sx={{
                  width: "180px",
                  "& .MuiOutlinedInput-root": { backgroundColor: "white" },
                }}
              />
              {errors.uMin && (
                <FormHelperText error>{errors.uMin}</FormHelperText>
              )}
            </Box>
            <Typography sx={{ mx: 2 }}>to</Typography>
            <Box>
              <TextField
                name="u_max"
                value={formValues.u_max}
                onChange={handleEnhancedInputChange}
                size="small"
                error={!!errors.uMax}
                sx={{
                  width: "180px",
                  "& .MuiOutlinedInput-root": { backgroundColor: "white" },
                }}
              />
              {errors.uMax && (
                <FormHelperText error>{errors.uMax}</FormHelperText>
              )}
            </Box>
          </Box>
        </Grid>

        {/* Modified WtMin Section */}
        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              backgroundColor: "#f5f5f5",
              p: 2,
              borderRadius: "8px",
            }}
          >
            <Typography
              sx={{
                width: "180px",
                fontWeight: 600,
                color: "#2c3e50",
              }}
            >
              Wt Min: *
            </Typography>
            <Box>
              <TextField
                name="wt_min"
                value={formValues.wt_min}
                onChange={handleEnhancedInputChange}
                size="small"
                error={!!errors.wtMin}
                sx={{
                  width: "180px",
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "white",
                  },
                }}
              />
              {errors.wtMin && (
                <FormHelperText error>{errors.wtMin}</FormHelperText>
              )}
            </Box>
            <Typography sx={{ mx: 2 }}>to</Typography>
            <Box>
              <TextField
                name="wt_max"
                value={formValues.wt_max}
                onChange={handleEnhancedInputChange}
                size="small"
                error={!!errors.wtMax}
                sx={{
                  width: "180px",
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "white",
                  },
                }}
              />
              {errors.wtMax && (
                <FormHelperText error>{errors.wtMax}</FormHelperText>
              )}
            </Box>
          </Box>
        </Grid>

        {/* Ship Message Section */}
        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              backgroundColor: "#f5f5f5",
              p: 2,
              borderRadius: "8px",
            }}
          >
            <Typography
              sx={{
                width: "180px",
                fontWeight: 600,
                color: "#2c3e50",
              }}
            >
              Ship Message: *
            </Typography>
            <TextField
              multiline
              rows={2}
              name="ship_message"
              value={formValues.ship_message || " "}
              onChange={handleInputChange}
              sx={{
                width: "400px",
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "white",
                },
              }}
            />
          </Box>
        </Grid>

        {/* Carrier Section */}
        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              backgroundColor: "#f5f5f5",
              p: 2,
              borderRadius: "8px",
            }}
          >
            <Typography
              sx={{
                width: "180px",
                fontWeight: 600,
                color: "#2c3e50",
              }}
            >
              Carrier: *
            </Typography>
            <Select
              name="carrier"
              value={formValues.carrier || ""}
              onChange={handleInputChange}
              size="small"
              sx={{
                width: "400px",
                backgroundColor: "white",
              }}
            >
              {carriers.map((carrier) => (
                <MenuItem key={carrier.scac_id} value={carrier.scac_id}>
                  {carrier.scac_name}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Grid>

        {/* Service Level Section */}
        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              backgroundColor: "#f5f5f5",
              p: 2,
              borderRadius: "8px",
            }}
          >
            <Typography
              sx={{
                width: "180px",
                fontWeight: 600,
                color: "#2c3e50",
              }}
            >
              Service Level: *
            </Typography>
            <Select
              name="service_level"
              value={formValues.service_level || ""}
              onChange={handleInputChange}
              size="small"
              sx={{
                width: "400px",
                backgroundColor: "white",
              }}
            >
              {serviceLevels.map((level) => (
                <MenuItem
                  key={level.service_level_id}
                  value={level.service_level_id}
                >
                  {level.service_level}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default RoutingGuideForm;
